import { useState } from 'react';
import toast from 'react-hot-toast';
import { addFeedbackApi } from 'services/auth/feedbackApis';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

const STAR_LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

function StarRating({ value, onChange }) {
    const [hovered, setHovered] = useState(0);

    return (
        <div className="flex items-center gap-1 mt-2">
            {[1, 2, 3, 4, 5].map((star) => {
                const active = star <= (hovered || value);
                return (
                    <button
                        key={star}
                        type="button"
                        aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                        onMouseEnter={() => setHovered(star)}
                        onMouseLeave={() => setHovered(0)}
                        onClick={() => onChange(star)}
                        className="focus:outline-none transition-transform duration-150 hover:scale-110"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className="w-9 h-9"
                            fill={active ? '#f59e0b' : 'none'}
                            stroke={active ? '#f59e0b' : '#d1d5db'}
                            strokeWidth="1.5"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                            />
                        </svg>
                    </button>
                );
            })}
            {(hovered || value) > 0 && (
                <span className="ml-2 text-sm font-medium text-amber-600">
                    {STAR_LABELS[hovered || value]}
                </span>
            )}
        </div>
    );
}

function Feedback() {
    const [rating, setRating] = useState(0);
    const [feedback, setFeedback] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (rating === 0) {
            toast.error('Please select a star rating.');
            return;
        }
        toast.loading('Submitting your feedback...');
        try {
            await addFeedbackApi(`${rating} star${rating > 1 ? 's' : ''}`, feedback);
            setRating(0);
            setFeedback('');
        } catch (error) {
            console.error('Error submitting feedback:', error);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center backdrop-blur z-10 py-8">
            <Card className="w-full max-w-lg rounded-2xl shadow-lg border border-neutral-200 bg-white">
                <CardHeader className="pb-2 border-b border-neutral-100">
                    <CardTitle className="text-2xl font-bold text-neutral-800 text-center">We Value Your Feedback</CardTitle>
                    <div className="flex justify-center mt-2">
                        <p className="text-amber-600 bg-amber-50 border border-amber-100 px-3 py-1.5 rounded-md inline-block text-sm font-medium"><strong>Note:</strong> All fields are mandatory.</p>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <Label className="text-sm font-medium text-neutral-700">Rate your experience *</Label>
                            <StarRating value={rating} onChange={setRating} />
                        </div>
                        <div>
                            <Label htmlFor="feedback" className="text-sm font-medium text-neutral-700">Feedback *</Label>
                            <Textarea
                                id="feedback"
                                name="feedback"
                                className="mt-1 w-full border border-neutral-200 rounded-md bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-orange-200 text-sm min-h-[100px]"
                                placeholder="Enter your feedback"
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                required
                            />
                        </div>
                        <Button type="submit" size={"lg"} className="w-full hover:bg-amber-500 text-white transition duration-300">
                            Submit Feedback
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}

export default Feedback;